import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CheckCircle2, Shield, Wrench, Clock, DollarSign } from "lucide-react";
import { SEO } from "@/components/SEO";

export default function PrinceAlbertReplacement() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How much does roof replacement cost in Prince Albert?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A standard roof replacement in Prince Albert generally costs between $6,000 and $14,000. The final price depends on roof size, pitch, and material choices. We provide honest, upfront quotes with no hidden fees.",
        },
      },
      {
        "@type": "Question",
        name: "Do you install roofs in Prince Albert during the winter?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "While summer and fall are peak seasons, we can perform certain roof replacements and emergency repairs during the winter months using specialized cold-weather techniques.",
        },
      },
      {
        "@type": "Question",
        name: "What type of shingles are best for Prince Albert homes?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We highly recommend architectural fiberglass asphalt shingles or premium metal roofing for Prince Albert, as they offer superior resistance to wind uplift, heavy snow loads, and harsh temperature fluctuations.",
        },
      },
    ],
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "@id": "https://www.saskroofingcompany.com/#localbusiness",
    name: "Sask Roofing Company",
    url: "https://www.saskroofingcompany.com/",
    telephone: "+1-639-739-7969",
    email: "info@saskroofingcompany.com",
    areaServed: {
      "@type": "City",
      name: "Prince Albert",
      containedInPlace: { "@type": "State", name: "Saskatchewan" },
    },
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Roof Replacement",
    provider: {
      "@id": "https://www.saskroofingcompany.com/#localbusiness",
    },
    areaServed: {
      "@type": "City",
      name: "Prince Albert",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.saskroofingcompany.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Service Areas",
        item: "https://www.saskroofingcompany.com/areas",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Prince Albert",
        item: "https://www.saskroofingcompany.com/areas/prince-albert",
      },
      {
        "@type": "ListItem",
        position: 4,
        name: "Roof Replacement",
        item: "https://www.saskroofingcompany.com/areas/prince-albert/replacement",
      },
    ],
  };

  return (
    <>
      <SEO
        title="Roof Replacement Prince Albert | Sask Roofing Company"
        description="Premium roof replacement in Prince Albert. We install tough, durable roofs designed for northern Saskatchewan weather. Honest pricing and expert local crews."
        schema={[
          faqSchema,
          localBusinessSchema,
          serviceSchema,
          breadcrumbSchema,
        ]}
      />
      <div className="flex flex-col min-h-screen">
        <section className="relative py-24 md:py-32 bg-slate-900 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-40">
            <img
              src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/b963df3a-8576-4537-8ff4-9d7e9bcdc2c5.png&output=webp&q=60"
              alt="Prince Albert Roof Replacement"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="container relative z-10 px-4">
            <div className="max-w-3xl space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Roof Replacement Prince Albert
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed">
                Protect your Prince Albert home with a roofing system built for
                northern Saskatchewan. We specialize in high-durability roof
                replacements that stand up to heavy snow, severe cold, and
                summer storms. Get a fixed-price quote with no surprises.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Button
                  size="lg"
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                  asChild
                >
                  <Link to="/contact">Get Your Free Inspection</Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                  asChild
                >
                  <a href="tel:6397397969">Call (639) 739-7969</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="text-3xl font-bold">
                  When to Consider Roof Replacement
                </h2>
                <p className="text-lg text-muted-foreground">
                  In Prince Albert, your roof is your first line of defense
                  against the elements. Ignoring warning signs can lead to
                  extensive water damage, mold, and costly structural repairs.
                  If your roof is failing, a full replacement is the safest and
                  most cost-effective choice.
                </p>
                <div className="space-y-4 pt-4">
                  <h3 className="text-xl font-semibold">
                    Key indicators it's time for a new roof:
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Clock className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Lifespan:</strong> Your asphalt roof is nearing
                        or has passed the 20-year mark.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Shield className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Curling & Buckling:</strong> Shingles are
                        visibly distorted, leaving your roof vulnerable to wind
                        uplift.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Wrench className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Granule Accumulation:</strong> You notice a
                        buildup of shingle granules in your eavestroughs,
                        meaning the protective layer is gone.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <DollarSign className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Interior Leaks:</strong> Water stains on your
                        ceiling or in your attic after a heavy rain or snowmelt.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/f1796618-613f-4a0c-8e31-90786ba9c4af.png&output=webp&q=60"
                  alt="Inspecting a roof for replacement in Prince Albert"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container px-4">
            <div className="max-w-3xl mx-auto text-center space-y-6 mb-16">
              <h2 className="text-3xl font-bold">
                Engineered for Northern Saskatchewan
              </h2>
              <p className="text-lg text-muted-foreground">
                Prince Albert experiences intense winters and significant snow
                loads. We install roofing systems specifically designed to
                handle these harsh conditions, ensuring your home stays warm,
                dry, and protected.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">
                  Heavy Snow Load Protection
                </h3>
                <p className="text-muted-foreground">
                  We ensure your roof deck is structurally sound and install
                  premium underlayments to handle the weight and moisture of
                  deep winter snows without compromising your interior.
                </p>
              </div>
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">Ice Dam Defense</h3>
                <p className="text-muted-foreground">
                  Ice damming is a major issue in Prince Albert. We apply
                  comprehensive ice and water shielding along eaves, valleys,
                  and penetrations to stop water from backing up and leaking
                  inside.
                </p>
              </div>
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">Optimal Ventilation</h3>
                <p className="text-muted-foreground">
                  A breathing attic is a healthy attic. We calculate and install
                  the perfect balance of intake and exhaust ventilation to
                  prevent winter condensation and summer heat buildup.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container px-4">
            <div className="grid md:grid-cols-2 gap-8 mb-20">
              <div className="bg-muted/30 p-8 rounded-xl border shadow-sm border-l-4 border-primary">
                <h3 className="text-2xl font-bold mb-4">
                  Building Permits and Code in Prince Albert
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Navigating local building codes can be confusing, but we
                  handle it for you. In Prince Albert, a standard residential
                  roof replacement (where we are simply tearing off old shingles
                  and installing new ones over existing decking) typically does
                  not require a building permit. However, if your roof has
                  suffered structural damage—such as broken trusses from heavy
                  snow loads or rotted decking that needs extensive
                  replacement—a permit from the City of Prince Albert may be
                  required. We assess your roof's structural integrity during
                  our free inspection and manage all necessary permitting and
                  compliance, ensuring your new roof meets all local and
                  provincial building codes.
                </p>
              </div>
              <div className="bg-muted/30 p-8 rounded-xl border shadow-sm border-l-4 border-primary">
                <h3 className="text-2xl font-bold mb-4">
                  Insurance and Storm Claims in Prince Albert
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Prince Albert experiences severe winter storms and heavy snow
                  loads that can cause significant roof damage. If your roof has
                  been damaged by a storm or ice damming, dealing with insurance
                  can be stressful. We specialize in storm damage restoration
                  and frequently work with local insurance adjusters. If you
                  suspect damage, call us for a free inspection before filing a
                  claim. We will document the damage with detailed photos and
                  provide a comprehensive report for your adjuster, ensuring you
                  get the full coverage you are entitled to for a proper roof
                  replacement.
                </p>
              </div>
            </div>

            <div className="mb-20">
              <div className="bg-card p-8 rounded-xl border shadow-sm max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold mb-4 text-center">
                  Materials and Warranties for Prince Albert Roofs
                </h3>
                <p className="text-muted-foreground leading-relaxed text-center mb-6">
                  For Prince Albert's demanding northern climate, we typically
                  recommend premium architectural shingles from trusted
                  manufacturers like IKO, CertainTeed, or GAF, or highly durable
                  metal roofing systems. Specifically, we focus on materials
                  rated for extreme cold-weather installation and superior
                  ice-and-water shielding to combat the region's notorious ice
                  damming issues. These roofing systems are engineered to
                  withstand severe weather and come with robust manufacturer
                  warranties. When combined with our local workmanship warranty,
                  you get a roofing system that provides long-lasting peace of
                  mind against the harsh Saskatchewan elements.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      300+
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Roofs in Prince Albert
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      Cold
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Weather Rated
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      Premium
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Ice Shielding
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      100%
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Local Crews
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/f1796618-613f-4a0c-8e31-90786ba9c4af.png&output=webp&q=60"
                  alt="Prince Albert roofing installation"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="order-1 lg:order-2 space-y-6">
                <h2 className="text-3xl font-bold">
                  Prince Albert Roof Replacement Process
                </h2>
                <p className="text-lg text-muted-foreground">
                  Replacing your roof shouldn't be stressful. We handle
                  everything from the initial inspection to the final cleanup
                  with professionalism and clear communication.
                </p>
                <ol className="space-y-6 pt-4">
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      1
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Free Property Assessment
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        We inspect your current roof, attic ventilation, and
                        decking to identify the full scope of work required.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      2
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Transparent Quoting
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        You receive a firm, written quote. We explain your
                        material options and the exact costs involved. No sales
                        pressure.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      3
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Expert Installation
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        Our experienced crews remove the old materials, repair
                        any decking issues, and install your new roofing system
                        to manufacturer specifications.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      4
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Site Cleanup & Review
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        We leave your property cleaner than we found it, running
                        magnetic sweeps for stray nails, followed by a final
                        walkthrough with you.
                      </p>
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 bg-muted/10 border-t">
          <div className="container px-4 max-w-4xl mx-auto">
            <nav
              aria-label="Prince Albert roofing pages"
              className="bg-background p-6 rounded-xl border shadow-sm"
            >
              <p className="font-bold text-lg mb-4 text-foreground">
                More roofing services in Prince Albert:
              </p>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/areas/prince-albert"
                    className="text-primary hover:underline font-medium"
                  >
                    ← All Prince Albert roofing services (overview)
                  </Link>
                </li>
                <li>
                  <Link
                    to="/areas/prince-albert/repair"
                    className="text-primary hover:underline font-medium"
                  >
                    Roof Repair in Prince Albert
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/replacement"
                    className="text-primary hover:underline font-medium"
                  >
                    All Sask roofing services
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </section>

        <section className="py-20 bg-muted/30 border-t">
          <div className="container px-4 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Prince Albert Roof Replacement FAQ
            </h2>
            <Accordion
              type="single"
              collapsible
              className="w-full bg-background rounded-xl border p-2 shadow-sm"
            >
              <AccordionItem value="item-1" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  How much does roof replacement cost in Prince Albert?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  A standard roof replacement in Prince Albert generally costs
                  between $6,000 and $14,000. The final price depends on roof
                  size, pitch, accessibility, and material choices. We provide
                  honest, upfront quotes with no hidden fees.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  Do you install roofs in Prince Albert during the winter?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  While summer and fall are peak seasons, we can perform certain
                  roof replacements and emergency repairs during the winter
                  months using specialized cold-weather techniques to ensure
                  proper sealing.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  What type of shingles are best for Prince Albert homes?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  We highly recommend architectural fiberglass asphalt shingles
                  or premium metal roofing for Prince Albert. These materials
                  offer superior resistance to wind uplift, heavy snow loads,
                  and harsh temperature fluctuations compared to standard 3-tab
                  shingles.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        <section className="py-24 bg-primary text-primary-foreground text-center">
          <div className="container px-4 max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl md:text-5xl font-bold">
              Secure Your Home Today
            </h2>
            <p className="text-xl opacity-90">
              Contact Prince Albert's trusted roofing experts for a free
              inspection and a reliable, fixed-price quote.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 h-auto"
                asChild
              >
                <Link to="/contact">Get Your Free Roof Inspection</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20 text-lg px-8 py-6 h-auto"
                asChild
              >
                <a href="tel:6397397969">Call (639) 739-7969</a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
